import localeService from 'commons/localeService/localeService';
import axios from 'axios';
import { delay } from 'commons/utils';
import 'commons/api/apiAuthentication/apiAuthHTTPInterceptor';

const DEFAULT_TIMEOUT = 20000;
const DEFAULT_RETRIES = 2;
const DEFAULT_RETRY_DELAY = 300;
const RETRYABLE_METHODS = ['get', 'head', 'options'];

// File uploads and AI generation legitimately run far past the default ceiling
// - how long depends on the file size or the model, not on service health. They
// opt in to this instead of being cut off at DEFAULT_TIMEOUT.
export const LONG_TIMEOUT = 120000;

// A request may only be repeated when it is safe to send twice, and only when
// the failure is plausibly transient: a 5xx, a dropped connection or a timeout.
// Anything the server answered with a 4xx will fail again, and a cancellation
// was asked for by the caller.
function isRetryable(options, error) {
  if (!RETRYABLE_METHODS.includes((options.method || 'get').toLowerCase())) {
    return false;
  }

  if (error?.isCanceled) {
    return false;
  }

  return error?.isNetworkError || error?.isTimeout || error?.status >= 500;
}

function retryDelayFor(attempt, retryDelay) {
  // 300ms, 900ms, ... plus jitter so a service coming back up is not hit by
  // every open tab at the same moment.
  return retryDelay * 3 ** attempt + Math.random() * 100;
}

async function requestWithRetry(options, params, retries, retryDelay) {
  for (let attempt = 0; ; attempt++) {
    try {
      // Rebuilt per attempt: the auth interceptor writes apiKey/sig/time into
      // params, and every attempt needs a freshly signed timestamp.
      return await axios({
        ...options,
        params: {
          locale: localeService.getLocale(), // todo: optionally sprd locale,
          ...params,
        },
      });
    } catch (error) {
      if (attempt >= retries || !isRetryable(options, error)) {
        throw error;
      }

      await delay(retryDelayFor(attempt, retryDelay));
    }
  }
}

export default {
  request(opts = {}) {
    const {
      retries = DEFAULT_RETRIES,
      retryDelay = DEFAULT_RETRY_DELAY,
      ...axiosOpts
    } = opts;

    const options = Object.assign(
      {
        withCredentials: true,
        timeout: DEFAULT_TIMEOUT,
      },
      axiosOpts,
    );

    if (
      !(options.url.startsWith('http://') || options.url.startsWith('https://'))
    ) {
      if (options.url && options.url[0] !== '/') {
        options.url = `/${options.url}`;
      }

      if (options.baseUrl && options.baseUrl[SPRD.PLATFORM]) {
        options.url = `${options.baseUrl[SPRD.PLATFORM]}${options.url}`;
      } else if (options.subdomain) {
        options.url = `${localeService.getSprdUrl({
          subdomain: options.subdomain,
          omitLanguage: true,
        })}${options.url}`;
      }
    }

    options.url = `${
      (options.baseUrl && options.baseUrl[SPRD.PLATFORM]) || ''
    }${options.url}`;

    return requestWithRetry(options, opts.params, retries, retryDelay);
  },
};
